import { Component, HostListener, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { InfonavitCreditService } from 'src/app/services/infonavit-credit.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

declare const $: any;

interface PayrollApplicationOption {
    value: string;
    label: string;
    desc: string;
    icon: string;
    bg: string;
}

interface CalculationReferenceOption {
    value: string;
    label: string;
}

@Component({
    selector: 'app-infonavit-credit-tab',
    templateUrl: './infonavit-credit-tab.component.html',
    styleUrls: ['./infonavit-credit-tab.component.css', '../employee-tab-shared.css']
})
export class InfonavitCreditTabComponent implements OnInit {
    @Input() employee: any;

    readonly payrollApplicationOptions: PayrollApplicationOption[] = [
        { value: 'accumulative_with_limit', label: 'Acumulativo con límite de monto', desc: 'El descuento se acumula hasta alcanzar el monto límite', icon: 'infonavit-radio-coins.svg', bg: '#e2d5f6' },
        { value: 'accumulative_until_date', label: 'Acumulativo hasta la fecha', desc: 'El descuento se aplicará hasta la fecha término', icon: 'infonavit-radio-calendar.svg', bg: '#fef3d4' },
        { value: 'deposit_no_limit', label: 'Depósito sin límite', desc: 'El descuento se aplicará sin límite de monto', icon: 'infonavit-radio-credit-card.svg', bg: '#ccffdf' },
        { value: 'current_payroll_only', label: 'Solo en esta nómina', desc: 'El descuento se aplicará únicamente en esta nómina', icon: 'infonavit-radio-file-star.svg', bg: '#ffccf0' }
    ];

    readonly calculationReferenceOptions: CalculationReferenceOption[] = [
        { value: 'salary_percentage', label: '% del salario' },
        { value: 'fixed_fee', label: 'Cuota fija' },
        { value: 'uma_number', label: 'Núm. de UMA' }
    ];

    credit: any = null;
    events: any[] = [];

    loading = false;
    loadError = false;
    loadingEvents = false;

    form!: FormGroup;
    submitted = false;
    actionsOpen = false;

    constructor(
        private fb: FormBuilder,
        private infonavitCreditService: InfonavitCreditService,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Listado de colaboradores', action);
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            credit_type: [this.employee?.employee_type || '', Validators.required],
            per_ded_key: ['', Validators.required],
            credit_number: [''],
            payroll_application: ['deposit_no_limit', Validators.required],
            start_date: ['', Validators.required],
            end_date: [''],
            formula_or_amount: ['', Validators.required],
            discount_value: ['', Validators.required],
            amount_limit: [''],
            calculation_reference: ['salary_percentage', Validators.required]
        });
        this.fetchCredit();
    }

    get f() {
        return this.form.controls;
    }

    get employeeFullName(): string {
        if (!this.employee) return '';
        return [this.employee.first_name, this.employee.last_name, this.employee.mother_last_name].filter(Boolean).join(' ');
    }

    fetchCredit(): void {
        if (!this.employee?.id) return;
        this.loading = true;
        this.loadError = false;
        this.infonavitCreditService.get(this.employee.id).subscribe({
            next: (resp: any) => {
                this.credit = (resp && Object.keys(resp).length > 0) ? resp : null;
                this.loading = false;
                if (this.credit) {
                    this.fetchEvents();
                } else {
                    this.events = [];
                }
            },
            error: () => {
                this.credit = null;
                this.loading = false;
                this.loadError = true;
            }
        });
    }

    fetchEvents(): void {
        if (!this.employee?.id) return;
        this.loadingEvents = true;
        this.infonavitCreditService.events(this.employee.id).subscribe({
            next: (resp: any) => {
                this.events = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
                this.loadingEvents = false;
            },
            error: () => {
                this.events = [];
                this.loadingEvents = false;
            }
        });
    }

    toggleActionsDropdown(): void {
        this.actionsOpen = !this.actionsOpen;
    }

    closeActionsDropdown(): void {
        this.actionsOpen = false;
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        this.closeActionsDropdown();
    }

    exportCsv(): void {
        this.closeActionsDropdown();
        const headers = ['Campo', 'Valor'];
        const escape = (value: any) => {
            const str = String(value ?? '');
            return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
        };
        const lines = [headers.join(',')];
        if (this.credit) {
            lines.push(['Estado del crédito', this.creditStateLabel].map(escape).join(','));
            lines.push(['Tipo de crédito', this.creditTypeLabel].map(escape).join(','));
            lines.push(['Descuento mensual', this.monthlyDiscountLabel].map(escape).join(','));
            lines.push(['Clave Per.Ded.', this.credit.per_ded_key].map(escape).join(','));
            lines.push(['Número de crédito', this.credit.credit_number].map(escape).join(','));
            lines.push(['Aplicación a nómina', this.payrollApplicationLabel(this.credit.payroll_application)].map(escape).join(','));
            lines.push(['Vigencia', `${this.formatDate(this.credit.start_date)} - ${this.credit.end_date ? this.formatDate(this.credit.end_date) : 'Sin fecha de término'}`].map(escape).join(','));
            lines.push(['Monto o fórmula', this.credit.formula_or_amount].map(escape).join(','));
            lines.push(['Monto límite', this.credit.amount_limit ? this.formatCurrency(this.credit.amount_limit) : 'Sin límite'].map(escape).join(','));
        } else {
            lines.push(['Estado del crédito', 'Sin crédito registrado'].map(escape).join(','));
        }
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `credito-infonavit-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    openModal(): void {
        this.submitted = false;
        this.form.reset({
            credit_type: this.employee?.employee_type || '',
            per_ded_key: '',
            credit_number: '',
            payroll_application: 'deposit_no_limit',
            start_date: '',
            end_date: '',
            formula_or_amount: '',
            discount_value: '',
            amount_limit: '',
            calculation_reference: 'salary_percentage'
        });
        $('#infonavitCreditModal').modal('show');
    }

    closeModal(): void {
        $('#infonavitCreditModal').modal('hide');
    }

    selectPayrollApplication(value: string): void {
        this.form.get('payroll_application')?.setValue(value);
    }

    save(): void {
        this.submitted = true;
        if (this.form.invalid || !this.employee?.id) {
            this.form.markAllAsTouched();
            return;
        }

        const raw = this.form.value;
        const payload: any = {
            credit_type: raw.credit_type,
            per_ded_key: raw.per_ded_key,
            credit_number: raw.credit_number || null,
            payroll_application: raw.payroll_application,
            start_date: raw.start_date,
            end_date: raw.end_date || null,
            formula_or_amount: raw.formula_or_amount,
            discount_value: raw.discount_value,
            amount_limit: raw.amount_limit || null,
            calculation_reference: raw.calculation_reference
        };

        this.spinner.show();
        this.infonavitCreditService.add(this.employee.id, payload).subscribe({
            next: () => {
                this.spinner.hide();
                this.closeModal();
                this.utils.alertSwal({ title: 'Operación exitosa', text: 'El crédito INFONAVIT se registró correctamente', icon: 'success' });
                this.fetchCredit();
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo registrar el crédito INFONAVIT', icon: 'error' });
            }
        });
    }

    payrollApplicationLabel(value: any): string {
        return this.payrollApplicationOptions.find((o) => o.value === value)?.label || value || '--';
    }

    calculationReferenceLabel(value: any): string {
        return this.calculationReferenceOptions.find((o) => o.value === value)?.label || value || '--';
    }

    private latestEventOfType(type: string): any {
        const matches = this.events.filter((e) => e.event_type === type);
        if (!matches.length) return null;
        return matches.reduce((a, b) => (new Date(a.date).getTime() > new Date(b.date).getTime() ? a : b));
    }

    get latestGrantEvent(): any {
        return this.latestEventOfType('grant');
    }

    get latestDiscountEvent(): any {
        return this.latestEventOfType('discount');
    }

    get latestModificationEvent(): any {
        return this.latestEventOfType('modification');
    }

    get creditStateLabel(): string {
        return this.credit ? 'Con crédito registrado' : 'Sin crédito registrado';
    }

    get creditStateSubtext(): string {
        if (!this.credit) return 'Este trabajador no tiene créditos INFONAVIT registrados.';
        return `Vigente desde ${this.formatDate(this.credit.start_date)}`;
    }

    get creditTypeLabel(): string {
        return this.credit?.credit_type || 'N/A';
    }

    get monthlyDiscountLabel(): string {
        if (!this.credit) return '$0.00';
        return this.formatCurrency(this.credit.discount_value);
    }

    get lastMovementLabel(): string {
        if (!this.events.length) return 'N/A';
        const sorted = [...this.events].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        return this.formatDate(sorted[0].date);
    }

    formatCurrency(value: any): string {
        if (value === null || value === undefined || value === '') return '$0.00';
        const num = Number(value) || 0;
        return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    }

    formatDate(value: any): string {
        if (!value) return '--/--/----';
        const datePart = String(value).split('T')[0].split(' ')[0];
        const [yyyy, mm, dd] = datePart.split('-');
        if (!yyyy || !mm || !dd) return '--/--/----';
        return `${dd}/${mm}/${yyyy}`;
    }
}
